import { useEffect, useMemo, useState } from 'react';
import { api } from '../api/client';
import { isCompleted, isDraft, pandaDocUrl, statusLabel } from '../config/docStatus';
import type { GhlContact, GhlContext, PersonDocument } from '../types';

type Props = {
  ghl: GhlContext;
  initialContactId: string | null;
  onOpenDocument: (documentId: string) => void;
};

// Read-only history view: pick a person → see every PandaDoc document for them,
// including ones signed NATIVELY in PandaDoc before GHL existed. View opens the
// doc in PandaDoc; Download streams the signed PDF through our backend.
export function Documents({ ghl, initialContactId, onOpenDocument }: Props) {
  const [contact, setContact] = useState<GhlContact | null>(null);
  const [contactSearch, setContactSearch] = useState('');
  const [contactResults, setContactResults] = useState<GhlContact[]>([]);
  const [docs, setDocs] = useState<PersonDocument[]>([]);
  const [loading, setLoading] = useState(false);
  const [signedOnly, setSignedOnly] = useState(false);
  const [query, setQuery] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Pre-load the contact from a deep-link (?contactId=…).
  useEffect(() => {
    if (!initialContactId) return;
    api.getContact(initialContactId, ghl).then(setContact).catch((e) => setError(String(e)));
  }, [initialContactId, ghl]);

  // Debounced contact search.
  useEffect(() => {
    if (!contactSearch.trim()) {
      setContactResults([]);
      return;
    }
    const t = setTimeout(() => {
      api.searchContacts(contactSearch, ghl).then(setContactResults).catch((e) => setError(String(e)));
    }, 250);
    return () => clearTimeout(t);
  }, [contactSearch, ghl]);

  // Load the person's full document history once a contact is picked.
  useEffect(() => {
    if (!contact) { setDocs([]); return; }
    const name = `${contact.firstName ?? ''} ${contact.lastName ?? ''}`.trim();
    if (!name) { setDocs([]); return; }
    setLoading(true);
    setError(null);
    api
      .listPersonDocuments({ name, email: contact.email, contactId: contact.id }, ghl)
      .then(setDocs)
      .catch((e) => setError(String(e)))
      .finally(() => setLoading(false));
  }, [contact, ghl]);

  const visible = useMemo(() => {
    let list = signedOnly ? docs.filter((d) => isCompleted(d.status)) : docs;
    const needle = query.trim().toLowerCase();
    if (needle) {
      // Filter the already-loaded list live — match on name, status, or date so
      // "work order", "completed" and "2026-05" all work.
      list = list.filter((d) => {
        const hay = `${d.name} ${d.status.replace('document.', '')} ${d.dateCompleted || d.dateCreated || ''}`.toLowerCase();
        return hay.includes(needle);
      });
    }
    return list;
  }, [docs, signedOnly, query]);

  return (
    <div style={{ maxWidth: 720, margin: '0 auto', padding: 24 }}>
      {error && (
        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b', padding: 12, borderRadius: 6, marginBottom: 16 }}>
          {error}
        </div>
      )}

      <section style={{ marginBottom: 20 }}>
        <h2 style={{ fontSize: 14, fontWeight: 600, color: '#6b7280', marginBottom: 8 }}>Person</h2>
        {contact ? (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: 12, background: 'white', border: '1px solid #e5e7eb', borderRadius: 6 }}>
            <div>
              <div style={{ fontWeight: 500 }}>{contact.firstName} {contact.lastName}</div>
              <div style={{ fontSize: 13, color: '#6b7280' }}>{contact.email}{contact.phone ? ` · ${contact.phone}` : ''}</div>
            </div>
            <button onClick={() => { setContact(null); setContactSearch(''); setDocs([]); setQuery(''); }} style={btnGhost}>Change</button>
          </div>
        ) : (
          <>
            <input
              autoFocus
              value={contactSearch}
              onChange={(e) => setContactSearch(e.target.value)}
              placeholder="Search by name or email…"
              style={input}
            />
            {contactResults.length > 0 && (
              <ul style={{ listStyle: 'none', padding: 0, marginTop: 8, background: 'white', border: '1px solid #e5e7eb', borderRadius: 6, maxHeight: 240, overflow: 'auto' }}>
                {contactResults.map((c) => (
                  <li key={c.id}>
                    <button onClick={() => setContact(c)} style={{ ...btnPlain, width: '100%', textAlign: 'left', padding: 12 }}>
                      <div style={{ fontWeight: 500 }}>{c.firstName} {c.lastName}</div>
                      <div style={{ fontSize: 13, color: '#6b7280' }}>{c.email}</div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </section>

      {contact && (
        <section>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <h2 style={{ fontSize: 14, fontWeight: 600, color: '#6b7280', margin: 0 }}>
              Documents{loading ? '' : ` (${visible.length}${visible.length !== docs.length ? ` of ${docs.length}` : ''})`}
            </h2>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#6b7280', cursor: 'pointer' }}>
              <input type="checkbox" checked={signedOnly} onChange={(e) => setSignedOnly(e.target.checked)} />
              Signed only
            </label>
          </div>

          {docs.length > 0 && (
            <div style={{ position: 'relative', marginBottom: 10 }}>
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Filter documents by name, status or date…"
                style={{ ...input, paddingRight: query ? 60 : 10 }}
              />
              {query && (
                <button onClick={() => setQuery('')} style={{ position: 'absolute', right: 6, top: 5, ...btnGhost, padding: '4px 10px' }}>
                  Clear
                </button>
              )}
            </div>
          )}

          {loading ? (
            <p style={{ color: '#9ca3af', fontSize: 13 }}>Loading…</p>
          ) : visible.length === 0 ? (
            <p style={{ color: '#9ca3af', fontSize: 13 }}>
              {query.trim()
                ? `No documents match “${query.trim()}”.`
                : `No documents found for this person${signedOnly ? ' (signed)' : ''}.`}
            </p>
          ) : (
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: 6 }}>
              {visible.map((d) => (
                <li key={d.documentId} style={{ padding: 12, background: 'white', border: '1px solid #e5e7eb', borderRadius: 6 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis' }}>{d.name}</div>
                      <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>
                        {(d.dateCompleted || d.dateCreated || '').slice(0, 10)}
                        {d.dateCompleted ? ' · signed' : ''}
                      </div>
                    </div>
                    <StatusBadge status={d.status} />
                  </div>
                  <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                    {isCompleted(d.status) && (
                      <button
                        onClick={() =>
                          api.downloadDocument(d.documentId, ghl).catch((e) => setError(String(e)))
                        }
                        style={{ ...btnLink, cursor: 'pointer' }}
                      >
                        Download PDF
                      </button>
                    )}
                    <a
                      href={d.documentUrl || pandaDocUrl(d.documentId)}
                      target="_blank"
                      rel="noreferrer"
                      style={btnGhostLink}
                    >
                      Open in PandaDoc
                    </a>
                    {/* Editing only works on drafts — PandaDoc locks a document once it is sent. */}
                    {isDraft(d.status) && (
                      <button onClick={() => onOpenDocument(d.documentId)} style={btnGhost}>Open in editor</button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const done = isCompleted(status);
  const draft = isDraft(status);
  const bg = done ? '#ecfdf5' : draft ? '#f3f4f6' : '#eff6ff';
  const fg = done ? '#047857' : draft ? '#6b7280' : '#1d4ed8';
  return (
    <span style={{ background: bg, color: fg, fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 999, whiteSpace: 'nowrap' }}>
      {statusLabel(status)}
    </span>
  );
}

const input: React.CSSProperties = {
  width: '100%',
  padding: 10,
  border: '1px solid #d1d5db',
  borderRadius: 6,
  fontSize: 14,
};

const btnGhost: React.CSSProperties = {
  padding: '6px 12px',
  background: 'transparent',
  border: '1px solid #e5e7eb',
  borderRadius: 4,
  fontSize: 13,
  cursor: 'pointer',
};

const btnPlain: React.CSSProperties = {
  border: 'none',
  background: 'transparent',
  cursor: 'pointer',
  fontSize: 14,
};

const btnLink: React.CSSProperties = {
  padding: '6px 12px',
  background: '#2563eb',
  color: 'white',
  border: '1px solid #2563eb',
  borderRadius: 4,
  fontSize: 13,
  textDecoration: 'none',
};

const btnGhostLink: React.CSSProperties = {
  padding: '6px 12px',
  background: 'transparent',
  color: '#374151',
  border: '1px solid #e5e7eb',
  borderRadius: 4,
  fontSize: 13,
  textDecoration: 'none',
};
